/**
 * CanvasFormSnapshotLeftSideView.js
 * Created by zl952fc on 11/23/2020.
 */

import React, {useState, useEffect, useMemo} from 'react';
import styled from 'styled-components';
import {useDispatch, useSelector} from 'react-redux';
import {IconoirMaximize} from '@ey-xd/motif-icon';
import {
	actionIcPrint24px,
	navigationIcFullscreen24px,
	navigationIcFullscreenExit24px
} from '@ey/motif-react-wrapper/assets/icons/index';
import moment from 'moment';
import {labels} from '../../../util/utils';
import {EngagementStatus, rnhEngagementSources} from '../../../util/uiconstants';
import {clientSideEventNames} from '../../../util/uiconstants';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import HeaderList from './Header/HeaderList';
import LeftSideSnapshotSelect from './LeftSideSnapshotSelect';
import {
	clearRightSideSnapshotView,
	getSnapshotById,
	showHideRightSideSnapshotView
} from '../../../actions/Snapshot/snapshotdetailsactions';
import {getSnapshotsForDocument} from '../../../actions/Snapshot/snapshotsactions';
import Icon from '../BaseComponentWrappers/MotifIcon';
import {expandableGetFormById} from '../../../util/Ares/customHooks';
import {useUserPreferences} from '../../../util/customHooks';

function CanvasFormSnapshotLeftSideView(props) {
	const {actions, isSnapshot, formData, engagementId, documentId} = props;
	const [headerList, setHeaderList] = useState([]),
		[thisFormData, setThisFormData] = useState(formData),
		[currentHeaderId, setCurrentHeaderId] = useState(0),
		[selectedSectionId, setSelectedSectionId] = useState(undefined),
		[selectedSnapShot, setSelectedSnapShot] = useState({
			value: -1,
			label: labels.currentVersion,
			title: labels.currentVersion
		}),
		[isSnapshotView, setSnapshotView] = useState(false),
		[isLoading, setIsLoading] = useState(false),
		[isFullViewLeftSideSnapshot, setFullViewLeftSideSnapshot] = useState(false),
		[showHideSnapshotView, setShowHideSnapshotView] = useState(false);

	const userPreferences = useUserPreferences();
	const snapshotDetails = useSelector((store) => store.snapshotDetails?.snapshotData),
		engagement = useSelector((store) => store.engagement),
		showHideSnapshotViewValue = useSelector((store) => store.showHideLeftSideSnapshotView);
	const dispatch = useDispatch();

	const getForm = () => {
		setIsLoading(true);
		dispatch(expandableGetFormById(engagement, documentId))
			.then(() => {
				setIsLoading(false);
			})
			.catch(() => {
				setIsLoading(false);
			});
	};

	const onSnapshotChange = (option) => {
		//const currentSnapshot = snapShotList.find((fl) => fl.value === option.value);
		const snapshotId = option?.id || option?.value;
		if (option) {
			setSelectedSnapShot({
				value: snapshotId,
				label: option.userName,
				title: option.userName,
				createDate: option.createDate,
				userName: option.userName
			});
		}
		setSnapshotView(option?.value !== -1);
		//Trigger Custom event for Rail.
		window.dispatchEvent(
			new CustomEvent(clientSideEventNames.onSnapshotChange, {
				detail: {snapshotId: Number(snapshotId)}
			})
		);

		dispatch(clearRightSideSnapshotView(option === undefined ? -1 : Number(snapshotId)));

		// getSnapshotDetails for selected snapshot
		if (snapshotId > 0) {
			getSnapshotDetails(snapshotId, null);
		} else {
			getForm();
		}
	};

	const getSnapshotDetails = (snapshotId, sourceSnapshotId) => {
		setIsLoading(true);
		dispatch(getSnapshotById(engagementId, documentId, snapshotId, sourceSnapshotId))
			.then(() => {
				setIsLoading(false);
			})
			.catch(() => {
				setIsLoading(false);
			});
	};

	const getHeaderListForView = () => {
		let formattedHeaderList = [];
		if (thisFormData && thisFormData.headers && thisFormData.headers.length > 0) {
			formattedHeaderList = thisFormData.headers;
		}
		return formattedHeaderList;
	};

	//#region Form printing logic

	// Opens the form's print view for a given snapshot or the current version
	const openFormPrintView = () => {
		const _snapshotId = selectedSnapShot.value,
			snapshotUser =
				_snapshotId !== -1 ? selectedSnapShot.userName : `${userPreferences.firstName} ${userPreferences.lastName}`;
		let snapshotDate;

		if (_snapshotId !== -1) {
			snapshotDate = moment.utc(selectedSnapShot.createDate);
		}

		let formPrintUrl = `/canvasform/print?engagementid=${engagementId}&documentid=${documentId}&snapshotid=${_snapshotId}&snapshotuser=${encodeURIComponent(
			snapshotUser
		)}&isAutomatedPrint=true`;
		formPrintUrl += snapshotDate ? `&snapshotdate=${snapshotDate.format('YYYY-MM-DDTHH:mm:ss')}` : '';

		const printViewWindow = window.open(formPrintUrl);
	};
	//#endregion

	const showHeaderList = useMemo(() => {
		return (
			<HeaderList
				changesView
				isSnapshotView={isSnapshotView}
				headers={headerList}
				formData={thisFormData}
				headerId={currentHeaderId}
				selectedSectionId={selectedSectionId}
				setSelectedSectionId={setSelectedSectionId}
				engagementId={engagementId}
				documentId={documentId}
			/>
		);
	}, [headerList, isSnapshotView, currentHeaderId, selectedSectionId, setSelectedSectionId, engagementId, documentId]);

	const showToggleViewCurrent = () => {
		dispatch(showHideRightSideSnapshotView(!isFullViewLeftSideSnapshot));
		setShowHideSnapshotView(false);
		setFullViewLeftSideSnapshot(!isFullViewLeftSideSnapshot);
	};

	useEffect(() => {
		if (isSnapshot) {
			dispatch(getSnapshotsForDocument(engagementId, documentId));
		}
	}, []);

	useEffect(() => {
		setHeaderList(snapshotDetails);
	}, [snapshotDetails]);

	useEffect(() => {
		if (showHideSnapshotViewValue === true || showHideSnapshotViewValue === false) {
			setShowHideSnapshotView(!showHideSnapshotViewValue);
		}
	}, [showHideSnapshotViewValue]);

	useEffect(() => {
		const headerList = getHeaderListForView();
		setHeaderList(headerList);
	}, [thisFormData]);

	useEffect(() => {
		setThisFormData(formData);
	}, [formData]);

	return (
		<StyledCanvasFormChanges
			className={`CanvasFormSnapshotLeftSideView ${isFullViewLeftSideSnapshot ? 'FullView' : 'HalfView'} ${
				showHideSnapshotView ? 'HideView' : ''
			}`}
		>
			{!showHideSnapshotView && (
				<>
					<section className="CanvasFormChangesHeader">
						<section className="CanvasFormCurrentChangesHeader">
							<LeftSideSnapshotSelect defaultselectedSnapShot={selectedSnapShot} onSnapshotChange={onSnapshotChange} />
							{engagement &&
								engagement.engagementStatusId !== EngagementStatus.CanvasExternalAccessPortalV2 &&
								engagement.engagementSourceId !== rnhEngagementSources.ceapV2Protected && (
									<section className="IconPrint" onClick={() => openFormPrintView()} title={labels.print}>
										<Icon src={actionIcPrint24px} disabled={isFullViewLeftSideSnapshot} title={labels.print} />
									</section>
								)}
							<section
								className="IconToggleView"
								onClick={() => showToggleViewCurrent()}
								title={!isFullViewLeftSideSnapshot ? labels.fullView : labels.defaultView}
							>
								<Icon src={isFullViewLeftSideSnapshot ? IconoirMaximize : IconoirMaximize} />
							</section>
						</section>
					</section>
					<section className="CanvasFormChangesBody">
						<section className={`CanvasFormCurrentChangesBody`}>
							{isLoading ? <LoadingIndicator view="inlineView" styleName="relatedDocuments-loading" /> : showHeaderList}
						</section>
					</section>
				</>
			)}
		</StyledCanvasFormChanges>
	);
}

export default CanvasFormSnapshotLeftSideView;

const StyledCanvasFormChanges = styled.section`
	display: flex;
	flex-direction: column;
	overflow: hidden;
	height: 100%;
	width: calc(50% - var(--px-5));
	padding-right: var(--px-10);
	background-color: var(--neutrals-25);
	&.HideView {
		display: none;
	}
	&.FullView {
		width: 100%;
		.entity-content.entity-content-level-1 {
			.scotEntityContentMain {
				width: calc(100% - var(--px-150)) !important;
				padding-right: var(--px-10);
			}
			&.RelatedRisksContainer {
				.tree-trigger-children {
					.RelatedRisksCol {
						max-width: calc(100% - var(--px-225));
					}
				}
			}
			&.ControlRelatedObject {
				&.RelatedRisksContainer {
					.tree-trigger-children {
						.RelatedRisksCol {
							width: calc(100% - var(--px-200));
						}
					}
				}
			}
		}
	}
	&.HalfView {
		.entity-content.entity-content-level-1 {
			.scotEntityContentMain {
				width: calc(100% - var(--px-150)) !important;
				padding-right: var(--px-10);
			}
			.controlEntityContentMain {
				width: 100% !important;
			}
			&.ControlRelatedObject {
				&.RelatedRisksContainer {
					.tree-trigger-children {
						.RelatedRisksCol {
							width: calc(100% - var(--px-200));
						}
					}
				}
			}
		}
		.isa315-relate-itprocess-itapplications-body {
			max-height:var(--px-830);
			.isa315-relate-itprocess-itapplist-title-list-toolbar {
				align-items: center;
				.motif-search-input-wrapper {
					.motif-typeahead {
						.motif-input {
							width: 100% !important;
						}
					}
				}
				.StyledClickableIconLabel {
					display: none;
				}
			}
			.isa315-relate-itprocess-itapplist-title {
				.isa315-relate-itprocess-unassociated {
					display: none;
				}
			}
			.StyledISA315RelateItProcessItAppListItem {
				.header-section {
					.isa315relateitprocessitappentitynameitem {
						width: calc(100% - var(--px-40));
					}
					.isa315relateitprocessitapplistitem {
						display: none;
					}
					.StyledBodySpecialAccordionHeaderOverflow {
						.motif-dropdown-portal {
							display: flex;
							.motif-dropdown-trigger {
								display: flex;
							}
						}
					}
				}
			}
		}
		.StyledISA315ScotItApplicationBodyListWrapper {
			.StyledISA315ScotItApplicationBodyListItem {
				.StyledBodySpecialAccordionHeader {
					.ScotColumnHeader {
						width: calc(100% - var(--px-350));
					}
					.StrategyColumnHeader {
						display: none;
					}
				}
			}
		}
		.isa315-itapp-scot-body-itapp-list {
			.isa315-itapp-scot-body-itapp-item {
				.StyledBodySpecialAccordionHeader {
					.ItAppColumnHeader {
						width: calc(100% - var(--px-150));
					}
					.ControlsCountColumnHeader {
						display: none;
					}
				}
			}
		}
		.StyledISA315ItGCTestStrategyList {
			.StyledBodySpecialAccordionHeader {
				.ITProcessHeader {
					width: calc(100% - var(--px-210));
				}
				.ControlsGroup1 {
					display: none;
				}
			}
		}
		.StyledBodySpecialManageITSP {
			.isa315-itprocess-itrisk-list {
				.StyledBodySpecialAccordionHeader {
					.ItProcessColumnHeader {
						width: calc(100% - var(--px-200));
					}
					.ControlsGroup1 {
						display: none;
					}
				}
			}
		}
		.StyledISA315ItProcessItRiskList {
			.StyledBodySpecialAccordionHeader {
				.ItProcessColumnHeader {
					width: calc(100% - var(--px-320));
				}
				.ControlsGroup1 {
					display: none;
				}
			}
		}
	}
	.SharedFormLabel {
		margin: var(--px-20) var(--px-20) 0 var(--px-20);
		width: calc(100% - var(--px-25));
		box-shadow: 0 0 var(--px-6) 0 rgba(0, 0, 0, 0.2);
		.SharedFormLabelText {
			font-size: var(--px-14);
		}
	}
	.SelectedCanvasFormHeader {
		padding: 0;
		display: flex;
		align-items: center;
		width: calc(100% - var(--px-3));
		.selectedCanvasFormHeaderWrapper {
			display: flex;
			width: calc(100% - var(--px-150));
		}
		.FormTitleHeader {
			font-size: var(--px-16);
			margin: 0;
			display: inline-flex;
			width: calc(100% - var(--px-30));
			padding-right: var(--px-20);
			height: var(--px-24);
		}
		.header-validation {
			min-width: var(--px-65);
			height: var(--px-24);
		}
		.RightSideHeader {
			display: flex;
			width: var(--px-150);
			margin-left: auto;
			.header-validation {
				min-width: var(--px-65);
				height: var(--px-20);
			}
			.sectionBody-validation {
				display: inline-flex;
				max-width: var(--px-60);
				width: auto;
				margin-left: var(--px-10);
			}
			.custom-labels {
				display: flex;
				align-items: center;
				margin: 0;
				margin-left: auto;
				height: var(--px-24);
				width: var(--px-60);
				svg {
					width: var(--px-20) !important;
					height: var(--px-20) !important;
					padding: 0;
				}
				.hiddenIcon {
					display: none;
				}
				.showIcon {
					display: inline-block;
				}
				.edit-icon {
					margin-right: var(--px-10);
					height: var(--px-20);
				}
				.motif-dropdown {
					height: var(--px-24);
					margin: 0;
				}
			}
		}
	}
	.sectionIncompletePadding {
		padding-left: var(--px-30);
	}
	.bodyIncompletePadding {
		padding-left: 0;
	}
	.selectDropdown {
		display: inline-flex;
		width: calc(100% - var(--px-150));
		@media only screen and (max-width: 1280px) {
			width: calc(100% - var(--px-162));
		}
	}
	.CanvasFormChangesHeader {
		display: flex;
		padding: 0 0 var(--px-10) 0;
		width: 100%;
		.CanvasFormCurrentChangesHeader {
			display: flex;
			align-items: center;
			width: 100%;
			padding-right: var(--px-10);
			.CanvasFormChangesSelect {
				width: 85%;
				.react-select-container {
					.react-select__menu {
						z-index: 99;
					}
				}
				.selectText {
					display: flex;
					padding-right: var(--px-30);
					.ellipses {
						&#snapshotOptionNameLabel {
							width: 75%;
							padding-top: var(--px-4);
						}
					}
					.ellipses {
						&#snapshotOptionDateLabel {
							width: 25%;
							justify-content: flex-end;
						}
					}
				}
			}
			.CanvasFormChangesSelect label {
				top: var(--px-5);
			}
			.IconPrint {
				margin-left: var(--px-20);
				cursor: pointer;
				.motif-icon {
					svg {
						width: var(--px-24);
						height: var(--px-24);
					}
				}
				&.disabled {
					pointer-events: none;
					cursor: not-allowed;
					svg {
						fill: var(--neutrals-200);
					}
				}
			}
			.IconToggleView {
				margin-left: auto;
				.motif-icon {
					svg {
						width: var(--px-24);
						height: var(--px-24);
					}
				}
				&.disabled {
					pointer-events: none;
					cursor: not-allowed;
					svg {
						fill: var(--neutrals-200);
					}
				}
			}
		}
		.CanvasFormSnapshotChangesHeader {
			display: flex;
			align-items: center;
			width: 100%;
			.CanvasFormChangesSelect {
				width: 85%;
				.react-select-container {
					.react-select__menu {
						z-index: 99;
					}
				}
				.selectText {
					display: flex;
					padding-right: var(--px-30);
					.ellipses {
						&#snapshotOptionNameLabel {
							width: 75%;
							padding-top: var(--px-4);
						}
					}
					.ellipses {
						&#snapshotOptionDateLabel {
							width: 25%;
							justify-content: flex-end;
						}
					}
				}
			}
			.IconToggleView {
				margin-left: auto;
				&.disabled {
					pointer-events: none;
					cursor: not-allowed;
					svg {
						fill: var(--neutrals-200);
					}
				}
			}
		}
	}
	.CanvasFormChangesBody {
		display: flex;
		width: 100%;
		padding: var(--px-10) 0 0 0;
		height: calc(100% - var(--px-60));
		.CanvasFormCurrentChangesBody {
			display: flex;
			width: 100%;
			border-right: var(--px-1) solid var(--neutrals-300);
			position: relative;
			.motif-progress-loader {
				flex-direction: column;
				top: 40%;
				left: 40%;
			}
			&.FullViewCurrent {
				width: 99.7%;
				padding: 0;
			}
			&.FullViewSnapshot {
				&.CanvasFormCurrentChangesBody {
					width: 0;
					padding: 0;
				}
			}
			.SectionBodyData {
				min-width: 30%;
				max-width: 100%;
			}
			.SelectedCanvasFormBody {
				padding: 0;
				.SectionBodyAccordion {
					width: calc(100% - var(--px-5));
					margin: 0 var(--px-5) var(--px-15) 0;
					border: var(--px-1) solid var(--neutrals-300);
					&:hover {
						box-shadow: var(--px-1) var(--px-2) var(--px-6) var(--px-2) rgba(0, 0, 0, 0.22);
					}
					.accordionWrapper {
						padding: var(--px-10);
						cursor: pointer;
						.SelectedCanvasFormHeader {
							width: 100%;
						}
						.plusminusIcons {
							margin-right: var(--px-5);
						}
						.sectionName {
							font-size: var(--px-16);
						}
						.section-Wrapper {
							width: calc(100% - var(--px-140));
							margin-right: var(--px-10);
						}
						.ValidationIcon {
							svg {
								vertical-align: middle;
							}
							.validationcount {
								top: calc(var(--px-3) * -1);
							}
						}
					}
					.FormBodyContainer {
						.body-container {
							.ITProcessRelatedObject {
								.ITApplicationRelatedObject {
									.Entity-content-list {
										.entity-content-main {
											width: calc(100% - var(--px-100)) !important;
										}
										.entity-content-data {
											width: var(--px-90);
											display: inline-flex;
											align-items: flex-start;
											&.SecondCol {
												&.remove-btn {
													display: inline-flex;
													justify-content: flex-end;
													min-width: var(--px-50) !important;
													width: auto !important;
													max-width: var(--px-100);
													padding: 0;
													margin-left: auto;
													margin-right: var(--px-20);
												}
											}
										}
									}
								}
							}
							.ControlRelatedObject {
								.tree-trigger-children {
									.controlEntityContentMain {
										width: calc(100% - var(--px-230));
									}
								}
							}
						}
						.bodyWrapper {
							&.DefaultView:last-child {
								margin-bottom: 0;
							}
							.StyledBodySpecialFetch {
								min-height: unset;
								.StyledBodySpecialPagination {
									margin: 0;
								}
							}
						}
						.section-body {
							min-height: 10rem;
							.motif-progress-loader {
								top: 0;
								left: 0;
							}
							.section-container {
								.SectionBodyData {
									&.SingleLineInputBodyData,
									&.MultiLineInputBodyData {
										.inputFieldWrapper {
											width: 100%;
											.TextAreaEditorWrapper {
												width: 100%;
												.inputField {
													width: 100%;
												}
											}
										}
									}
								}
							}
						}
					}
					.RelatedObjectWrapperContainer {
						.SectionRelatedObjectWrapper {
							.header-container {
								.header-entity-name {
									&.single-column {
										width: calc(100% - var(--px-90)) !important;
									}
									&.four-column {
										width: calc(100% - var(--px-330)) !important;
										@media only screen and (max-width: 1280px) {
											width: calc(100% - var(--px-150)) !important;
										}
									}
								}
								.header-entity-data {
									&.four-column {
										width: var(--px-110) !important;
									}
								}
							}
							.body-container {
								.entity-content-level-1 {
									.bodyTree {
										.tree-content {
											.ITApplicationRelatedObject {
												.Entity-content-list {
													.entity-content-data {
														width: var(--px-90);
													}
												}
												&.entity-content-level-2 {
													.Entity-content-list {
														.entity-content-data {
															width: var(--px-110);
														}
													}
												}
											}
											.controlEntityContentWrapper {
												width: 54% !important;
												@media only screen and (max-width: 1280px) {
													width: 53% !important;
												}
											}
										}
									}
								}
								.ControlRelatedObject {
									.ITApplicationRelatedObject {
										.entity-content-main {
											width: calc(100% - var(--px-310)) !important;
										}
									}
									.tree-content {
										.ITApplicationRelatedObject {
											.Entity-content-list {
												.entity-content-main {
													width: calc(100% - var(--px-330)) !important;
													@media only screen and (max-width: 1280px) {
														width: calc(100% - var(--px-150)) !important;
													}
												}
											}
										}
									}
								}
							}
							.RelatedRisksContainer {
								.tree-trigger-children {
									.entity-content-data {
										width: var(--px-160);
									}
								}
							}
						}
					}
					.SelectedCanvasFormBody {
						width: 100%;
						.SectionBodyAccordion {
							&:last-child {
								margin-bottom: 0;
							}
						}
					}
					.FormBodyContainer {
						.SectionBodyWrapper {
							.section-showless-header {
								width: 100%;
								margin-right: var(--px-20);
							}
							.BadgeTextWrapper {
								display: inline-block;
							}
							.ItControlRelatedObject {
								.bodyTree {
									.tree-content {
										.ITApplicationRelatedObject {
											.Entity-content-list {
												.entity-content-main {
													width: calc(100% - var(--px-130)) !important;
												}
												.entity-content-data {
													width: var(--px-110) !important;
												}
											}
										}
									}
								}
							}
						}
					}
					.SelectedCanvasFormBody {
						padding: var(--px-10);
						height: initial;
						overflow: initial;
						.SectionBodyAccordion {
							width: 100%;
							margin-bottom: var(--px-10);
							padding: 0 0 0 var(--px-10);
							.RelatedObjectWrapperContainer {
								.SectionRelatedObjectWrapper {
									.header-container {
										.header-entity-name {
											&.single-column {
												width: calc(100% - var(--px-90)) !important;
											}
										}
									}
									.body-container {
										.entity-content-level-1 {
											.bodyTree {
												.tree-content {
													.ITApplicationRelatedObject {
														.Entity-content-list {
															.entity-content-data {
																width: var(--px-90);
															}
														}
														&.entity-content-level-2 {
															.Entity-content-list {
																.entity-content-data {
																	width: var(--px-110);
																}
															}
														}
													}
													.controlEntityContentWrapper {
														width: 54% !important;
														@media only screen and (max-width: 1280px) {
															width: 53% !important;
														}
													}
												}
											}
										}
										.ControlRelatedObject {
											.ITApplicationRelatedObject {
												.entity-content-main {
													width: calc(100% - var(--px-310)) !important;
												}
											}
											.tree-content {
												.ITApplicationRelatedObject {
													.Entity-content-list {
														.entity-content-main {
															width: calc(100% - var(--px-320)) !important;
															@media only screen and (max-width: 1280px) {
																width: calc(100% - var(--px-150)) !important;
															}
														}
													}
												}
											}
										}
									}
									.RelatedRisksContainer {
										.tree-trigger-children {
											.entity-content-data {
												width: var(--px-160);
											}
										}
									}
								}
							}
						}
					}
				}
			}
		}
		.CanvasFormSnapshotChangesBody {
			display: flex;
			width: 100%;
			&.FullViewSnapshot {
				width: 99.7%;
				padding: 0;
			}
			&.FullViewCurrent {
				&.CanvasFormSnapshotChangesBody {
					width: 0;
					padding: 0;
				}
			}
			.SelectedCanvasFormBody {
				padding: 0;
				width: 100%;
				.SectionBodyAccordion {
					width: 100%;
					margin-bottom: var(--px-15);
					.accordionWrapper {
						padding: var(--px-10);
						cursor: pointer;
						.plusminusIcons {
							margin-right: var(--px-5);
						}
					}
					.SelectedCanvasFormBody {
						padding: var(--px-10) var(--px-10) 0 var(--px-10);
						.SectionBodyAccordion {
							width: 100%;
							margin-bottom: var(--px-10);
							min-height: var(--px-46);
						}
					}
				}
			}
		}
	}
	&.FullView {
		.SectionRelatedObjectWrapper {
			.header-entity-name {
				width: 77%;
			}
		}
		.CanvasFormCurrentChangesBody {
			border-right: 0;
		}
	}
`;
